// src/lib/cartSession.ts
import { api } from "./api";

const CART_KEY = "cart.items";
const CART_ID_KEY = "cart.id";
const CART_META_KEY = "cart.meta";

export type CartMap = Record<string, number>;

function getOrCreateCartId(): string {
  let cid = "anon";
  try {
    cid = localStorage.getItem(CART_ID_KEY) || "";
    if (!cid) {
      cid =
        (crypto as any)?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(CART_ID_KEY, cid);
    }
  } catch {}
  return cid || "anon";
}

export function getCartId(): string {
  try { return localStorage.getItem(CART_ID_KEY) || getOrCreateCartId(); }
  catch { return getOrCreateCartId(); }
}

export function cartAuthHeaders(): Record<string,string> {
  const cid = getCartId();
  return { 'X-Cart-Id': cid };
}

export function readCart(): CartMap {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function writeCart(next: CartMap) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(next || {}));
    window.dispatchEvent(new CustomEvent("cart:update"));
  } catch {}
}

export function readCartMeta(): { totalQty: number; totalAmount: number; updatedAt?: number } {
  try {
    const raw = localStorage.getItem(CART_META_KEY);
    return raw ? JSON.parse(raw) : { totalQty: 0, totalAmount: 0 };
  } catch {
    return { totalQty: 0, totalAmount: 0 };
  }
}

export function writeCartMeta(meta: { totalQty: number; totalAmount: number }) {
  try {
    localStorage.setItem(
      CART_META_KEY,
      JSON.stringify({ ...meta, updatedAt: Date.now() })
    );
    window.dispatchEvent(new CustomEvent("cart:update"));
  } catch {}
}

let syncTimer: number | undefined;

/** Envoie l’instantané du panier au serveur (debounced) */
export function syncCartToServer(summary: {
  items: Array<{ id: string; qty: number; price?: number; label?: string; brand?: string }>;
  totalQty: number;
  totalAmount: number;
  context?: "tarifs" | "recap" | "other";
}) {
  const cartId = getOrCreateCartId();

  writeCartMeta({ totalQty: summary.totalQty, totalAmount: summary.totalAmount });

  if (syncTimer) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(async () => {
    try {
      await api.post("/cart-sessions/sync", {
        cartId,
        items: summary.items,
        totalQty: summary.totalQty,
        totalAmount: summary.totalAmount,
        context: summary.context || "other",
      });
    } catch {
      // silencieux
    }
  }, 800);
}

/** Rattache le panier anonyme au user connecté (à appeler après login) */
export async function attachCartToUser() {
  const cartId = getOrCreateCartId();
  try {
    await api.post("/cart-sessions/attach-user", { cartId });
  } catch {}
}

/** 🧹 Vide local + demande au serveur de supprimer la session panier */
export async function clearCartOnServerAndLocal() {
  const cartId = getCartId();
  try {
    await api.post("/cart-sessions/sync", {
      cartId,
      items: [],
      totalQty: 0,
      totalAmount: 0,
      context: "other",
    });
  } catch {}

  try {
    localStorage.setItem(CART_KEY, JSON.stringify({}));
    localStorage.setItem(CART_META_KEY, JSON.stringify({ totalQty: 0, totalAmount: 0, updatedAt: Date.now() }));
    window.dispatchEvent(new CustomEvent("cart:update"));
  } catch {}
}